import { DateTime } from "luxon";

export default class DebtPanel {
  constructor({
    dealType,
    yearsFormatted = [],
    purchasePrice,
    debtAssumed,
    transCoAndRest,
    cash,
    minimumCashBalance,
    inputArray = [],
    cashPayForAddition,
    FCFforDebtService,
    dividendsPercent,
    SOFRCurrent,
    SOFRStep,
    PIKStep,
  }) {
    this.dealType = dealType;
    this.yearsFormatted = yearsFormatted;
    this.purchasePrice = purchasePrice;
    this.debtAssumed = debtAssumed;
    this.transCoAndRest = transCoAndRest;
    this.cash = cash;
    this.minimumCashBalance = minimumCashBalance;
    this.inputArray = inputArray;
    this.cashPayForAddition = cashPayForAddition;
    this.FCFforDebtService = FCFforDebtService;
    this.dividendsPercent = dividendsPercent;
    this.SOFRCurrent = SOFRCurrent;
    this.SOFRStep = SOFRStep;
    this.PIKStep = PIKStep;
  }

  get debtArray() {
    let { inputArray, sponsorEquity, debtTotal } = this;
    inputArray.filter((debt) => debt.name === "Sponsor Equity")[0].value = sponsorEquity;
    inputArray.map((debt) => {
      debt.percent = debt.value / debtTotal * 100;
      if(typeof debt.period === "number") debt.totalTerm = debt.period + debt.moratorium;   //calculate Total Term
    })
    return inputArray;
  }

  get feesTotal() {
    const { inputArray } = this;
    return inputArray.reduce((acc, debt) => acc + (debt.value * debt.fee / 100 || 0), 0);
  }

  get fundsTotal() {
    //Excel: Sheet("Sources&Uses").Range("H9")
    const { purchasePrice, debtAssumed, transCoAndRest, feesTotal } = this;
    return purchasePrice + debtAssumed + transCoAndRest + feesTotal;
  }

  get debtTotal() {
    //Excel: Sheet("Stock - Investor DCF").Range("G132")
    //This calculation is calculated from fundsTotal, as sponsor equity is calculated from fundsTotal
    const { fundsTotal, cash, minimumCashBalance } = this;
    return fundsTotal - cash + minimumCashBalance;
  }

  get sponsorEquity() {
    const { debtTotal, inputArray } = this;
    return debtTotal - inputArray.filter((debt) => debt.name !== "Sponsor Equity").reduce((acc, debt) => acc + debt.value, 0);
  }

  get debtInclude() {
    const { debtArray } = this;
    return debtArray
      .reduce((acc, debt) => acc + (debt.name !== "Sponsor Equity" ? debt.value : 0), 0);
  }

  get debtExclude() {
    const { debtArray, debtInclude } = this;
    return debtInclude - debtArray.filter((debt) => debt.name === "Owner Financing")[0].value;
  }

  get ratioInclude() {
    const { debtInclude, sponsorEquity } = this;
    return debtInclude / (debtInclude + sponsorEquity) * 100;
  }

  get ratioExclude() {
    const { debtExclude, sponsorEquity } = this;
    return debtExclude / (debtExclude + sponsorEquity) * 100;
  }

  get debtAndCashEngine() {
    const {
      yearsFormatted,
      debtArray,
      cash,
      cashPayForAddition,
      FCFforDebtService,
      minimumCashBalance,
      dividendsPercent,
      SOFRStep,
      PIKStep,
    } = this;
    let SOFRCurrent = this.SOFRCurrent;

    //variables
    let yearsIncludingBaseYear = [...yearsFormatted];
    yearsIncludingBaseYear.splice(0, 0, DateTime.fromJSDate(new Date(yearsFormatted[0])).plus({ years: -1 }).toFormat("LLLL y"));
    let totalPeriod = yearsFormatted.length;
    let PIKDeltaCurrent = 0;

    //results
    let ownerFinancing = {
      ...debtArray.filter((debt) => debt.name === "Owner Financing")[0],
      monthlyPayments: 0,
      balloonPayment: 0,
      cashSweepTotal: 0,
      totalInterest: 0,
      cashFlowArray: yearsFormatted.map((year) => ({ date: year, value: 0 })),
      IRR: 0,
      EM: 0
    };   //duplicate owner financing Assumptions and add necessary items
    ownerFinancing.monthlyPayments = ownerFinancing.value / ownerFinancing.period;  //calculate monthly payments
    ownerFinancing.cashFlowArray.splice(0, 0, {
      date: DateTime.fromJSDate(new Date(yearsFormatted[0])).plus({ years: -1 }).toFormat("LLLL y"),
      value: -ownerFinancing.value
    });      //insert base year's cash flow

    let lineOfCredit = {
      ...debtArray.filter((debt) => debt.name === "Line of Credit")[0],
      cashFlowArray: yearsFormatted.map((year) => ({ date: year, value: 0 })),
      IRR: 0,
      EM: 0
    };    //duplicate line of credit assumptions and add necessary items
    lineOfCredit.cashFlowArray.splice(0, 0, {
      date: DateTime.fromJSDate(new Date(yearsFormatted[0])).plus({ years: -1 }).toFormat("LLLL y"),
      value: -lineOfCredit.value
    });      //insert base year's cash flow

    let otherDebtsArray = debtArray.filter((debt) =>
      debt.name !== "Owner Financing" &&
      debt.name !== "Line of Credit" &&
      debt.name.indexOf("Equity") < 0
    ).map((debt) => ({
      ...debt,
      linearPay: debt.value / debt.period,
      cashFlowArray: yearsIncludingBaseYear.map((year) => ({ date: year, value: 0 })),
      IRR: 0,
      EM: 0
    }));  //duplicate other debts Ass, add linear repay amount
    let cashBalance = minimumCashBalance;      //this sum is begin cash balance at first
    let cashBalArray = yearsFormatted.map((year) => ({ date: year, value: 0 }));
    let cashAvailableForEquity = FCFforDebtService.map((obj) => ({...obj}));   //start with FCF, add or delete in/outcome(F95)
    let debtSumArray = yearsFormatted.map((year) => ({ date: year, value: 0, disabled: false }));
    let repaymentArray = yearsFormatted.map((year) => ({ date: year, value: 0, disabled: false }));

    //Main Engine
    for(let i = 0; i < totalPeriod; i++) {
      SOFRCurrent += SOFRStep;      //set SOFR for that period(needed for floating interest rate calculation)
      PIKDeltaCurrent += PIKStep;   //set PIK delta for that period(needed for PIK interest rate calculation)
      let sweepRepay = 0;
      let repaymentSum = 0;         //total of principal and interest for all debts in that year
      let debtIncome = 0;           //total cash income/inflow of debts. We calculate this for equity holder distribution

      //// Debt Cash Income and Compulsory Principal Repay & Interest Expense Outcome

      //Line of Credit(interest only)
      let validRateOfLOC;    //interest rate decided by fixed or floating
      if(lineOfCredit.interestType === "Fixed") validRateOfLOC = lineOfCredit.rate;
      else validRateOfLOC = Math.max((SOFRCurrent / 100 + lineOfCredit.rate), lineOfCredit.floor);
      cashAvailableForEquity[i].value -= lineOfCredit.value * validRateOfLOC / 100; //subtract interest from cash Bal.
      repaymentSum += lineOfCredit.value * validRateOfLOC / 100;
      lineOfCredit.cashFlowArray[i + 1].value += lineOfCredit.value * validRateOfLOC / 100;
        //add interest payment to the cash flow array(plus one is because array has base year)

      //Owner Financing
      //if(i === 0) cashAvailableForEquity[i].value += ownerFinancing.value;    //cash from Owner Financing comes into C.A. //currently suspended
      for(let j = 0; j < 12 && ownerFinancing.value > 0 && ownerFinancing.balloonMonth > 0; j++) {
        //calculation for Owner Financing, because it is monthly
        //we don't count total term, because Owner Financing amount will be zero before than the total term
        ownerFinancing.balloonMonth--;
        ownerFinancing.moratorium--;
        let monthlyInterest =  ownerFinancing.value * ownerFinancing.rate / 12 / 100;
        let repayment = 0;
        //Interest handle
        ownerFinancing.totalInterest += monthlyInterest;    //total interest calculate
        cashAvailableForEquity[i].value -= monthlyInterest;       //subtract interest from the available cash balance
        repaymentSum += monthlyInterest;                          //total prinicpal + interest for that year
        //compulsory repay handle
        if(ownerFinancing.moratorium < 0) repayment = Math.min(ownerFinancing.value, ownerFinancing.monthlyPayments);
          //if the left amount is smaller than regular pay, repay amount is the same as the left amount
        if(ownerFinancing.balloonMonth === 0) {     //if it is balloon month, we repay all(except regular pay) as the balloon
          ownerFinancing.balloonPayment = ownerFinancing.value - repayment;     //except regular pay
          repayment = ownerFinancing.value;          //total principal repayment that month(repay all)
        }
        ownerFinancing.value -= repayment;    //Owner Financing balance decrease
        cashAvailableForEquity[i].value -= repayment;       //subtract principal repayment from the available cash balance
        repaymentSum += repayment;                          //total prinicpal + interest for that year
        ownerFinancing.cashFlowArray[i + 1].value += monthlyInterest + repayment;   //plus one is because array has base year
          //add monthly interest and repayment to the cash flow array. Remember that cashFlowArray is yearly!!!
      }

      //Other Debts
      otherDebtsArray.map((debt) => {
        if(debt.startYear <= i + 1 && debt.startYear + debt.totalTerm - 1 >= i + 1 && debt.value > 0) { //debt period
          let validRate;    //interest rate decided by fixed or floating
          let repayment = 0;
          //principal income handle
          if(debt.startYear === i + 1) {
            // if(debt.name !== "Assumed Debt") {           //currently suspended
            //   cashAvailableForEquity[i].value += debt.value; //increase cash bal except Assumed LT Debt
            //   debtIncome += debt.value;                      //calculate cash inflow
            // }
            debt.cashFlowArray[i].value = -debt.value;     //debt arises in the start of the year. It is the same as the previous year
          }
          //interest handle
          if(debt.interestType === "Fixed") validRate = debt.rate;
          else validRate = Math.max((SOFRCurrent / 100 + debt.rate), debt.floor);   //floating rate can't smaller than floor
          cashAvailableForEquity[i].value -= debt.value * validRate / 100;  //subtract interest from the cash balance
          repaymentSum += debt.value * validRate / 100;                     //total prinicpal + interest for that year
          debt.cashFlowArray[i + 1].value += debt.value * validRate / 100;  //interest payment of debt cash flow(+1: base year)
          //compulsory repay handle
          if(debt.repayType === "Linear") {   //linear schedule
            if(debt.startYear + debt.moratorium <= i + 1) repayment = Math.min(debt.value, debt.linearPay);  //repay linearly
          }
          else {    //bullet or PIK
            if(debt.startYear + debt.totalTerm - 1 === i + 1) repayment = debt.value;   //repay all amount final year
          }
          debt.value -= repayment;    //debt amount decrease
          cashAvailableForEquity[i].value -= repayment;       //subtract principal repayment from the available cash balance
          repaymentSum += repayment;                          //total prinicpal + interest for that year
          debt.cashFlowArray[i + 1].value += repayment;       //principal payment of debt cash flow(+1: base year)
          //PIK handle
          if(debt.repayType === "PIK") debt.value += debt.value * (PIKDeltaCurrent / 100 + debt.pikRate) / 100;
        }
      });

      //// Cash Sweep

      //Line of Credit
      sweepRepay = Math.min(lineOfCredit.value, cashAvailableForEquity[i].value + cashBalance - minimumCashBalance);
        //when cash < 0, lineOfCredit and cash increases both
      if(sweepRepay < 0) sweepRepay = Math.max(sweepRepay, Math.min(0, lineOfCredit.value - lineOfCredit.capacity));
        //the amount of line of credit can't excess lineOfCredit.capacity. Math.min(0, ...) is the left capacity increase limit
      lineOfCredit.value -= sweepRepay;
      cashAvailableForEquity[i].value -= sweepRepay;
      if(sweepRepay > 0) repaymentSum += sweepRepay;                          //total prinicpal + interest for that year
      lineOfCredit.cashFlowArray[i + 1].value += sweepRepay;
        //add interest payment to the cash flow array(plus one is because array has base year), LOC arises in the end of year

      //sweep for Owner Financing. don't forget to decrease ownerFinancing.value
      if(ownerFinancing.value > 0) {
        sweepRepay = Math.min(ownerFinancing.value * ownerFinancing.sweep / 100, Math.max(0, cashAvailableForEquity[i].value));
          //we consider sweep rate of debt and sweepRepay can't less than 0
        ownerFinancing.value -= sweepRepay;
        ownerFinancing.cashSweepTotal += sweepRepay;
        cashAvailableForEquity[i].value -= sweepRepay;
        repaymentSum += sweepRepay;                                 //total prinicpal + interest for that year
        ownerFinancing.cashFlowArray[i + 1].value += sweepRepay;
          //add cash sweep to the cash flow array(plus one is because that array has a base year value)
      }

      //other debt sweep
      otherDebtsArray.map((debt) => {
        if(debt.startYear <= i + 1 && debt.startYear + debt.totalTerm - 1 > i + 1 && debt.value > 0) { //except last year
          sweepRepay = Math.min(debt.value * debt.sweep / 100, Math.max(0, cashAvailableForEquity[i].value));
          debt.value -= sweepRepay;
          cashAvailableForEquity[i].value -= sweepRepay;
          repaymentSum += sweepRepay;                                 //total prinicpal + interest for that year
          debt.cashFlowArray[i + 1].value += sweepRepay;              //sweep repay of debt cash flow(+1: base year)
        }
      })

      ////Cash Balance Calculation
      let cashForDistribution = cashAvailableForEquity[i].value - debtIncome;
      cashBalance += debtIncome + cashForDistribution * (cashForDistribution > 0 ? (1 - dividendsPercent / 100) : 1);
      cashBalArray[i].value = cashBalance;

      //total debt balance
      let debtSum = 0;
      debtSum += lineOfCredit.value;
      debtSum += ownerFinancing.value;
      otherDebtsArray.map((debt) => {debtSum += debt.value;});
      debtSumArray[i].value = debtSum;

      //repayment
      repaymentArray[i].value = repaymentSum;
    }

    //get invalid cash balance year and value
    let lowestCashBal = cashBalArray.reduce((lowest, cashBal) => (lowest.value > cashBal.value ? cashBal : lowest), {value: Infinity});
    let invalidBal = lowestCashBal.value < minimumCashBalance - 0.0001 ?
      { date: lowestCashBal.date, value: minimumCashBalance - lowestCashBal.value } : false;

    return {
      ownerFinancing,
      lineOfCredit,
      otherDebtsArray,
      debtSumArray,
      repaymentArray,
      cashBalance,
      cashBalArray,
      invalidBal,
      cashAvailableForEquity
    };
  }

}
